import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Divider, Grid2, InputAdornment, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FiShuffle, FiSearch, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import POSHeading from '@/components/POS/Common/POSHeading';
import POSButton from '@/components/POS/Common/POSButton';
import POSInput from '@/components/POS/Common/POSInput';
import POSTextArea from '@/components/POS/Common/POSTextArea';
import POSSelect from '@/components/POS/Common/POSSelect';
import POSSwitch from '@/components/POS/Common/POSSwitch';
import POSDatePicker from '@/components/POS/Common/POSDatePicker';
import { bookingDiscountsApi } from '../Core/booking-discounts.api';
import {
    BookingDiscount,
    BookingDiscountFormValues,
    CreateBookingDiscountPayload,
    CustomerType,
    DiscountChannel,
    TreatmentGroup,
    TreatmentItem,
} from '../Types/booking-discounts.types';
import {
    generateRandomDiscountCode,
    bookingDiscountValidationSchema,
    DISCOUNT_CODE_MAX_LENGTH,
} from '../Core/booking-discounts.schema';

export default function BookingDiscountForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id: routeId } = useParams<{ id?: string }>();
    const isEditMode = Boolean(routeId);

    const [existingDiscount, setExistingDiscount] = useState<BookingDiscount | null>(null);
    const [isLoadingDiscount, setIsLoadingDiscount] = useState<boolean>(isEditMode);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isGeneratingCode, setIsGeneratingCode] = useState<boolean>(false);
    const [treatmentsList, setTreatmentsList] = useState<TreatmentGroup[]>([]);
    const [isLoadingTreatments, setIsLoadingTreatments] = useState<boolean>(true);
    const [treatmentSearch, setTreatmentSearch] = useState<string>('');
    const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
    const [isHeaderStuck, setIsHeaderStuck] = useState(false);
    const stickySentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoadingDiscount) return;

        const sentinel = stickySentinelRef.current;
        if (!sentinel) return;

        const scrollRoot = sentinel.closest('.content-wrapper');
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsHeaderStuck(!entry.isIntersecting);
            },
            { root: scrollRoot instanceof Element ? scrollRoot : null, threshold: 0 },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isLoadingDiscount]);

    useEffect(() => {
        if (!isEditMode || !routeId) {
            setExistingDiscount(null);
            setIsLoadingDiscount(false);
            return;
        }

        let isMounted = true;
        (async () => {
            setIsLoadingDiscount(true);
            try {
                const singleData = await bookingDiscountsApi.getBookingDiscountById(routeId);
                if (isMounted) {
                    setExistingDiscount(singleData);
                }
            } catch (error) {
                console.error('Error fetching booking discount details:', error);
            } finally {
                if (isMounted) {
                    setIsLoadingDiscount(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [isEditMode, routeId]);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                setIsLoadingTreatments(true);
                const fetchedTreatments = await bookingDiscountsApi.fetchTreatmentsList();
                if (isMounted) {
                    setTreatmentsList(fetchedTreatments);
                }
            } catch (error) {
                console.error('Error fetching treatments list:', error);
            } finally {
                if (isMounted) {
                    setIsLoadingTreatments(false);
                }
            }
        })();
        return () => {
            isMounted = false;
        };
    }, []);

    const initialValues: BookingDiscountFormValues = useMemo(() => {
        if (existingDiscount) {
            const isNoExpiry = !existingDiscount.expiresAt;
            const formattedExpiry = existingDiscount.expiresAt
                ? moment(existingDiscount.expiresAt).format('YYYY-MM-DD')
                : '';

            const serviceIdsArray = Array.isArray(existingDiscount.serviceIds)
                ? (existingDiscount.serviceIds as unknown as number[])
                : [];
            const isAllTreatments = serviceIdsArray.length === 0;

            const customerType: CustomerType = existingDiscount.customerType || 'ALL';
            const hasUsageLimit =
                typeof existingDiscount.maxUsesPerCustomer === 'number' && existingDiscount.maxUsesPerCustomer > 0;

            const activeChannels = existingDiscount.channels || [];

            return {
                code: existingDiscount.code || '',
                description: existingDiscount.description || '',
                active: existingDiscount.isActive ?? true,
                noExpiry: isNoExpiry,
                expiresAt: formattedExpiry,
                discountType:
                    existingDiscount.discountType === 'FIXED_AMOUNT' ? ('fixed' as const) : ('percentage' as const),
                value: Number(existingDiscount.discountValue) || 10,
                allTreatments: isAllTreatments,
                serviceIds: serviceIdsArray,
                customerType,
                hasUsageLimit,
                maxUsesPerCustomer: existingDiscount.maxUsesPerCustomer || 1,
                allowOnlineBooking: activeChannels.includes('ONLINE_BOOKING'),
                allowGiftCards: activeChannels.includes('GIFT_CARD'),
                allowPunchCards: activeChannels.includes('PUNCH_CARD'),
            };
        }

        return {
            code: '',
            description: '',
            active: true,
            noExpiry: false,
            expiresAt: moment().add(3, 'months').format('YYYY-MM-DD'),
            discountType: 'percentage' as const,
            value: 10,
            allTreatments: true,
            serviceIds: [],
            customerType: 'ALL' as CustomerType,
            hasUsageLimit: false,
            maxUsesPerCustomer: 1,
            allowOnlineBooking: true,
            allowGiftCards: false,
            allowPunchCards: false,
        };
    }, [existingDiscount]);

    const formik = useFormik<BookingDiscountFormValues>({
        initialValues,
        validationSchema: bookingDiscountValidationSchema,
        enableReinitialize: true,
        onSubmit: async (formValues: BookingDiscountFormValues) => {
            setIsSaving(true);
            try {
                const selectedChannels: DiscountChannel[] = [];
                if (formValues.allowOnlineBooking) {
                    selectedChannels.push('ONLINE_BOOKING');
                }
                if (formValues.allowGiftCards) {
                    selectedChannels.push('GIFT_CARD');
                }
                if (formValues.allowPunchCards) {
                    selectedChannels.push('PUNCH_CARD');
                }

                const isCodeLocked = Boolean(isEditMode && existingDiscount?.isUsed);

                const payload: CreateBookingDiscountPayload = {
                    ...(isCodeLocked ? {} : { code: formValues.code.trim().toUpperCase() }),
                    description: formValues.description.trim() || null,
                    isActive: formValues.active,
                    expiresAt:
                        formValues.noExpiry || !formValues.expiresAt
                            ? null
                            : moment(formValues.expiresAt).endOf('day').toISOString(),
                    discountType: formValues.discountType === 'fixed' ? 'FIXED_AMOUNT' : 'PERCENTAGE',
                    discountValue: Number(formValues.value),
                    serviceIds: formValues.allTreatments ? null : formValues.serviceIds,
                    customerType: formValues.customerType,
                    maxUsesPerCustomer: formValues.hasUsageLimit
                        ? Math.max(1, Number(formValues.maxUsesPerCustomer))
                        : null,
                    channels: selectedChannels,
                };

                if (isEditMode && existingDiscount?.id) {
                    await bookingDiscountsApi.updateBookingDiscount(existingDiscount.id, payload);
                    toast.success(t('BookingDiscounts.ToastUpdatedSuccess'));
                } else {
                    await bookingDiscountsApi.createBookingDiscount({
                        ...payload,
                        code: formValues.code.trim().toUpperCase(),
                    });
                    toast.success(t('BookingDiscounts.ToastCreatedSuccess'));
                }
                navigate('/booking-discounts');
            } catch (error) {
                console.error('Error saving booking discount:', error);
                const axiosError = error as { response?: { data?: { message?: string } } };
                const errorMessage = axiosError?.response?.data?.message || '';
                if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('unique')) {
                    toast.error(t('BookingDiscounts.ToastErrorDuplicate'));
                } else {
                    toast.error(t('BookingDiscounts.ToastErrorSave'));
                }
            } finally {
                setIsSaving(false);
            }
        },
    });

    const isCodeLocked = Boolean(isEditMode && existingDiscount?.isUsed);

    const handleGenerateCode = async () => {
        if (isGeneratingCode || isCodeLocked) return;

        setIsGeneratingCode(true);
        try {
            const apiCodeResult = await bookingDiscountsApi.generateDiscountCode();
            if (apiCodeResult?.code) {
                formik.setFieldValue('code', apiCodeResult.code.toUpperCase().slice(0, DISCOUNT_CODE_MAX_LENGTH));
            } else {
                formik.setFieldValue('code', generateRandomDiscountCode());
            }
        } catch (error) {
            formik.setFieldValue('code', generateRandomDiscountCode());
        } finally {
            setIsGeneratingCode(false);
        }
    };

    const groupedTreatments = useMemo(() => {
        const query = treatmentSearch.trim().toLowerCase();

        return treatmentsList
            .map((groupItem: TreatmentGroup) => {
                const filteredServices = query
                    ? groupItem.services.filter((treatmentItem: TreatmentItem) =>
                          treatmentItem.name.toLowerCase().includes(query),
                      )
                    : groupItem.services;

                return {
                    ...groupItem,
                    title: groupItem.groupId === 0 ? t('Services.ServiceWithoutGRP') : groupItem.title,
                    services: filteredServices,
                };
            })
            .filter((groupItem: TreatmentGroup) => groupItem.services.length > 0);
    }, [treatmentsList, treatmentSearch, t]);

    const isAllGroupSelected = (treatmentIdsList: number[]): boolean => {
        return treatmentIdsList.every((treatmentIdItem: number) => formik.values.serviceIds.includes(treatmentIdItem));
    };

    const toggleTreatmentSelection = (treatmentId: number, checkedState: boolean) => {
        const currentServiceIds = formik.values.serviceIds;
        if (checkedState) {
            formik.setFieldValue('serviceIds', [...Array.from(new Set([...currentServiceIds, treatmentId]))]);
        } else {
            formik.setFieldValue(
                'serviceIds',
                currentServiceIds.filter((selectedId: number) => selectedId !== treatmentId),
            );
        }
    };

    const toggleCategoryGroupSelection = (treatmentIdsList: number[], checkedState: boolean) => {
        const currentServiceIds = formik.values.serviceIds;
        if (checkedState) {
            const mergedSet = new Set([...currentServiceIds, ...treatmentIdsList]);
            formik.setFieldValue('serviceIds', Array.from(mergedSet));
        } else {
            formik.setFieldValue(
                'serviceIds',
                currentServiceIds.filter((selectedId: number) => !treatmentIdsList.includes(selectedId)),
            );
        }
    };

    if (isLoadingDiscount) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack sx={{ p: { xs: 0, md: 2 } }}>
            <Box ref={stickySentinelRef} sx={{ height: 1, width: '100%', mt: -0.125 }} aria-hidden />
            <Stack
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    bgcolor: '#fff',
                    py: 1.5,
                    // Cancel layout (xs:2/md:4) + form (xs:0/md:2) padding so border spans full content width
                    mx: { xs: -2, md: -6 },
                    px: { xs: 2, md: 6 },
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1,
                    width: 'auto',
                    borderBottom: isHeaderStuck ? '1px solid #E5E5E5' : '1px solid transparent',
                    transition: 'border-bottom-color 0.15s ease',
                }}
            >
                <POSHeading
                    text={isEditMode ? t('BookingDiscounts.EditDiscountCode') : t('BookingDiscounts.NewDiscountCode')}
                    sx={{ width: '100%', mt: 1 }}
                />

                <Stack
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1,
                        width: '100%',
                    }}
                >
                    <POSButton
                        title={t('BookingDiscounts.Cancel')}
                        variant="f_outline"
                        width={{ xs: '100%', md: 'auto' }}
                        onClick={() => navigate('/booking-discounts')}
                    />
                    {formik.dirty && (
                        <POSButton
                            title={
                                isSaving
                                    ? t('BookingDiscounts.Saving')
                                    : isEditMode
                                      ? t('BookingDiscounts.SaveChanges')
                                      : t('BookingDiscounts.Save')
                            }
                            variant="save"
                            width={{ xs: '100%', md: 'auto' }}
                            onClick={() => formik.submitForm()}
                            disabled={isSaving || isGeneratingCode}
                        />
                    )}
                </Stack>
            </Stack>

            <form
                style={{
                    width: '100%',
                    backgroundColor: '#fff',
                    borderRadius: '25px',
                    marginTop: 10,
                }}
                onSubmit={formik.handleSubmit}
            >
                {/* Section 1: Code & Basic Info */}
                <Grid2 container spacing={{ xs: 0, md: 3 }} sx={{ mt: 2, p: { xs: 2, md: 4 } }}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('BookingDiscounts.CodeHeader')} />
                        <POSHeading
                            text={t('BookingDiscounts.EditorSubtitle')}
                            sx={{ fontSize: 16, color: '#afafaf', fontWeight: 400 }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 8 }}
                        sx={{
                            mt: { xs: 2, md: 0 },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 4, xl: 5 },
                        }}
                    >
                        <Stack>
                            <POSHeading
                                text={`${t('BookingDiscounts.CodeLabel')} ${t('BookingDiscounts.CodeLabelHint', { length: `4 - ${DISCOUNT_CODE_MAX_LENGTH} ` })}`}
                                fontSize={16}
                            />
                            <POSInput
                                name="code"
                                placeholder={t('BookingDiscounts.CodePlaceholder')}
                                value={formik.values.code}
                                disabled={isCodeLocked}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    formik.setFieldValue(
                                        'code',
                                        event.target.value.toUpperCase().slice(0, DISCOUNT_CODE_MAX_LENGTH),
                                    )
                                }
                                slotProps={{ htmlInput: { maxLength: DISCOUNT_CODE_MAX_LENGTH } }}
                                endIcon={
                                    isCodeLocked ? undefined : (
                                        <Box
                                            onClick={handleGenerateCode}
                                            sx={{
                                                cursor: isGeneratingCode ? 'default' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                pointerEvents: isGeneratingCode ? 'none' : 'auto',
                                                opacity: isGeneratingCode ? 0.7 : 1,
                                            }}
                                        >
                                            {isGeneratingCode ? (
                                                <CircularProgress size={14} color="inherit" />
                                            ) : (
                                                <FiShuffle size={14} />
                                            )}
                                            {t('BookingDiscounts.Generate')}
                                        </Box>
                                    )
                                }
                            />
                            <Typography
                                variant="caption"
                                color={
                                    isCodeLocked || (formik.touched.code && formik.errors.code)
                                        ? 'error'
                                        : 'text.secondary'
                                }
                                sx={{ mt: 0.5 }}
                            >
                                {isCodeLocked
                                    ? t('BookingDiscounts.CodeLockedUsedHint')
                                    : formik.touched.code && formik.errors.code
                                      ? t(formik.errors.code as string)
                                      : ''}
                            </Typography>
                        </Stack>

                        <Stack>
                            <POSHeading text={t('BookingDiscounts.DescriptionLabel')} fontSize={16} />
                            <POSTextArea
                                name="description"
                                placeholder={t('BookingDiscounts.DescriptionPlaceholder')}
                                value={formik.values.description}
                                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                    formik.setFieldValue('description', event.target.value);
                                }}
                            />
                        </Stack>

                        <Stack>
                            <POSHeading text={t('BookingDiscounts.ActiveCodeLabel')} fontSize={16} />
                            <POSSwitch
                                name="active"
                                checked={formik.values.active}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    formik.setFieldValue('active', event.target.checked)
                                }
                                label={t('BookingDiscounts.ActiveCodeLabel')}
                            />
                        </Stack>
                    </Grid2>
                </Grid2>

                <Divider sx={{ borderWidth: 2, width: '100%', borderColor: '#D2D2D2' }} />

                {/* Section 2: Discount Value & Type */}
                <Grid2 container spacing={{ xs: 0, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('BookingDiscounts.DiscountCardTitle')} />
                        <POSHeading
                            text={t('BookingDiscounts.DiscountHeader')}
                            sx={{ fontSize: 16, color: '#afafaf', fontWeight: 400 }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 8 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 4, xl: 5 },
                        }}
                    >
                        <Stack>
                            <POSHeading text={t('BookingDiscounts.TypeHeader')} fontSize={16} />
                            <POSSelect
                                value={formik.values.discountType}
                                onChange={(event) =>
                                    formik.setFieldValue('discountType', event.target.value as 'percentage' | 'fixed')
                                }
                                options={[
                                    {
                                        label: `${t('BookingDiscounts.Percentage')} (%)`,
                                        value: 'percentage',
                                    },
                                    {
                                        label: `${t('BookingDiscounts.FixedAmount')} (DKK)`,
                                        value: 'fixed',
                                    },
                                ]}
                            />
                        </Stack>

                        <Stack>
                            <POSHeading text={t('BookingDiscounts.DiscountCardTitle')} fontSize={16} />
                            <POSInput
                                name="value"
                                value={formik.values.value}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const rawValue = event.target.value;
                                    if (rawValue === '') {
                                        formik.setFieldValue('value', '');
                                        return;
                                    }
                                    const parsedValue = parseFloat(rawValue);
                                    if (!Number.isNaN(parsedValue)) {
                                        formik.setFieldValue('value', parsedValue);
                                    }
                                }}
                                error={formik.touched.value && Boolean(formik.errors.value)}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {formik.values.discountType === 'percentage' ? '%' : 'DKK'}
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            {formik.touched.value && formik.errors.value && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                    {t(formik.errors.value as string)}
                                </Typography>
                            )}
                        </Stack>
                    </Grid2>
                </Grid2>

                <Divider sx={{ borderWidth: 2, width: '100%', borderColor: '#D2D2D2' }} />

                {/* Section 3: Expiry Date */}
                <Grid2 container spacing={{ xs: 0, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('BookingDiscounts.ExpiryCardTitle')} />
                        <POSHeading
                            text={t('BookingDiscounts.NoExpiryHint')}
                            sx={{ fontSize: 16, color: '#afafaf', fontWeight: 400 }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 8 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 4, xl: 5 },
                        }}
                    >
                        <Stack>
                            <POSHeading text={t('BookingDiscounts.NoExpiryTitle')} fontSize={16} />
                            <POSSwitch
                                name="noExpiry"
                                checked={formik.values.noExpiry}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const isNoExpiryChecked = event.target.checked;
                                    formik.setFieldValue('noExpiry', isNoExpiryChecked);
                                    if (!isNoExpiryChecked && !formik.values.expiresAt) {
                                        formik.setFieldValue(
                                            'expiresAt',
                                            moment().add(3, 'months').format('YYYY-MM-DD'),
                                        );
                                    }
                                }}
                                label={t('BookingDiscounts.NoExpiryTitle')}
                            />
                        </Stack>

                        {!formik.values.noExpiry && (
                            <Stack>
                                <POSHeading text={t('BookingDiscounts.ExpiresOn')} fontSize={16} />
                                <POSDatePicker
                                    value={formik.values.expiresAt ? moment(formik.values.expiresAt) : moment()}
                                    onChange={(selectedDate) => {
                                        formik.setFieldValue(
                                            'expiresAt',
                                            selectedDate && selectedDate.isValid()
                                                ? selectedDate.format('YYYY-MM-DD')
                                                : '',
                                        );
                                        formik.setFieldTouched('expiresAt', true, false);
                                    }}
                                    format="DD/MM-YYYY"
                                    disablePast
                                    sx={{ borderRadius: 2, width: '100%' }}
                                />
                                {formik.touched.expiresAt && formik.errors.expiresAt && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                        {t(formik.errors.expiresAt as string)}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Grid2>
                </Grid2>

                <Divider sx={{ borderWidth: 2, width: '100%', borderColor: '#D2D2D2' }} />

                {/* Section 4: Eligible Treatments / Services */}
                <Grid2 container spacing={{ xs: 0, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('BookingDiscounts.TreatmentsCardTitle')} />
                        <POSHeading
                            text={t('BookingDiscounts.TreatmentsCardHint')}
                            sx={{ fontSize: 16, color: '#afafaf', fontWeight: 400 }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 8 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 4, xl: 5 },
                        }}
                    >
                        <Stack>
                            <POSHeading text={t('BookingDiscounts.AllTreatmentsTitle')} fontSize={16} />
                            <POSSwitch
                                name="allTreatments"
                                checked={formik.values.allTreatments}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    formik.setFieldValue('allTreatments', event.target.checked)
                                }
                                label={t('BookingDiscounts.AllTreatmentsTitle')}
                            />
                        </Stack>

                        {!formik.values.allTreatments && (
                            <Stack spacing={2}>
                                <POSInput
                                    placeholder={t('BookingDiscounts.SearchTreatmentsPlaceholder')}
                                    value={treatmentSearch}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        setTreatmentSearch(event.target.value)
                                    }
                                    startIcon={<FiSearch size={16} />}
                                />

                                {formik.touched.serviceIds && formik.errors.serviceIds && (
                                    <Typography variant="caption" color="error">
                                        {t(formik.errors.serviceIds as string)}
                                    </Typography>
                                )}

                                <Box
                                    sx={{
                                        maxHeight: 380,
                                        overflowY: 'auto',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        p: 1.5,
                                    }}
                                >
                                    {isLoadingTreatments ? (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            {t('Common.Loading')}
                                        </Typography>
                                    ) : groupedTreatments.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            {t('BookingDiscounts.NoTreatmentsFound')}
                                        </Typography>
                                    ) : (
                                        groupedTreatments.map((groupItem: TreatmentGroup) => {
                                            const groupKey = String(groupItem.groupId);
                                            const categoryTreatmentIds = groupItem.services.map(
                                                (treatmentItem: TreatmentItem) => treatmentItem.id,
                                            );
                                            const isCategoryCollapsed = collapsedCategories[groupKey];

                                            return (
                                                <Box
                                                    key={groupKey}
                                                    sx={{
                                                        mb: 1.5,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        sx={{
                                                            px: 2,
                                                            py: 1,
                                                            bgcolor: 'action.hover',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1}
                                                            onClick={() =>
                                                                setCollapsedCategories((previousState) => ({
                                                                    ...previousState,
                                                                    [groupKey]: !previousState[groupKey],
                                                                }))
                                                            }
                                                        >
                                                            {isCategoryCollapsed ? (
                                                                <FiChevronRight size={16} />
                                                            ) : (
                                                                <FiChevronDown size={16} />
                                                            )}
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                {groupItem.title} ({groupItem.services.length})
                                                            </Typography>
                                                        </Stack>

                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {t('BookingDiscounts.SelectAll')}
                                                            </Typography>
                                                            <POSSwitch
                                                                checked={isAllGroupSelected(categoryTreatmentIds)}
                                                                onChange={(
                                                                    event: React.ChangeEvent<HTMLInputElement>,
                                                                ) =>
                                                                    toggleCategoryGroupSelection(
                                                                        categoryTreatmentIds,
                                                                        event.target.checked,
                                                                    )
                                                                }
                                                            />
                                                        </Stack>
                                                    </Stack>

                                                    {!isCategoryCollapsed && (
                                                        <Stack divider={<Divider />}>
                                                            {groupItem.services.map((treatmentItem: TreatmentItem) => (
                                                                <Stack
                                                                    key={treatmentItem.id}
                                                                    direction="row"
                                                                    justifyContent="space-between"
                                                                    alignItems="center"
                                                                    sx={{ px: 2, py: 1.25 }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{ fontWeight: 500 }}
                                                                        >
                                                                            {treatmentItem.name}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {treatmentItem.price} DKK
                                                                        </Typography>
                                                                    </Box>
                                                                    <POSSwitch
                                                                        checked={formik.values.serviceIds.includes(
                                                                            treatmentItem.id,
                                                                        )}
                                                                        onChange={(
                                                                            event: React.ChangeEvent<HTMLInputElement>,
                                                                        ) =>
                                                                            toggleTreatmentSelection(
                                                                                treatmentItem.id,
                                                                                event.target.checked,
                                                                            )
                                                                        }
                                                                    />
                                                                </Stack>
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            </Stack>
                        )}
                    </Grid2>
                </Grid2>

                <Divider sx={{ borderWidth: 2, width: '100%', borderColor: '#D2D2D2' }} />

                {/* Section 5: Target Customers & Usage Limits */}
                <Grid2 container spacing={{ xs: 0, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('BookingDiscounts.CustomerTypeTitle')} />
                        <POSHeading
                            text={t('BookingDiscounts.CustomerTypeHint')}
                            sx={{ fontSize: 16, color: '#afafaf', fontWeight: 400 }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 8 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 4, xl: 5 },
                        }}
                    >
                        <Stack>
                            <POSHeading text={t('BookingDiscounts.CustomerTypeTitle')} fontSize={16} />
                            <POSSelect
                                value={formik.values.customerType}
                                onChange={(event) =>
                                    formik.setFieldValue('customerType', event.target.value as CustomerType)
                                }
                                options={[
                                    {
                                        label: t('BookingDiscounts.LimitAll'),
                                        value: 'ALL',
                                    },
                                    {
                                        label: t('BookingDiscounts.LimitNew'),
                                        value: 'NEW',
                                    },
                                    {
                                        label: t('BookingDiscounts.LimitExisting'),
                                        value: 'EXISTING',
                                    },
                                ]}
                            />
                        </Stack>

                        <Stack>
                            <POSHeading text={t('BookingDiscounts.CustomerLimitTitle')} fontSize={16} />
                            <POSSwitch
                                name="hasUsageLimit"
                                checked={formik.values.hasUsageLimit}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    formik.setFieldValue('hasUsageLimit', event.target.checked)
                                }
                                label={t('BookingDiscounts.LimitYes')}
                            />
                        </Stack>

                        {formik.values.hasUsageLimit && (
                            <Stack>
                                <POSHeading text={t('BookingDiscounts.MaxUsesLabel')} fontSize={16} />
                                <POSInput
                                    type="number"
                                    name="maxUsesPerCustomer"
                                    value={formik.values.maxUsesPerCustomer}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const rawValue = event.target.value;
                                        if (rawValue === '') {
                                            formik.setFieldValue('maxUsesPerCustomer', '');
                                            return;
                                        }
                                        const parsedValue = parseInt(rawValue, 10);
                                        if (!Number.isNaN(parsedValue)) {
                                            formik.setFieldValue('maxUsesPerCustomer', parsedValue);
                                        }
                                    }}
                                    error={
                                        formik.touched.maxUsesPerCustomer && Boolean(formik.errors.maxUsesPerCustomer)
                                    }
                                />
                                {formik.touched.maxUsesPerCustomer && formik.errors.maxUsesPerCustomer && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                        {t(formik.errors.maxUsesPerCustomer as string)}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Grid2>
                </Grid2>

                <Divider sx={{ borderWidth: 2, width: '100%', borderColor: '#D2D2D2' }} />

                {/* Section 6: Redemption Channels */}
                <Grid2 container spacing={{ xs: 0, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('BookingDiscounts.ChannelsTitle')} />
                        <POSHeading
                            text={t('BookingDiscounts.ChannelsHint')}
                            sx={{ fontSize: 16, color: '#afafaf', fontWeight: 400 }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 8 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 4, xl: 5 },
                        }}
                    >
                        <POSSwitch
                            name="allowOnlineBooking"
                            checked={formik.values.allowOnlineBooking}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                formik.setFieldValue('allowOnlineBooking', event.target.checked)
                            }
                            label={t('BookingDiscounts.ChannelOnlineBooking')}
                        />
                        <POSSwitch
                            name="allowGiftCards"
                            checked={formik.values.allowGiftCards}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                formik.setFieldValue('allowGiftCards', event.target.checked)
                            }
                            label={t('BookingDiscounts.ChannelGiftCard')}
                        />

                        {/* Temporary disabled because punch cards are not being sold on marketplaces yet */}
                        {/* <POSSwitch
                            name="allowPunchCards"
                            checked={formik.values.allowPunchCards}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                formik.setFieldValue('allowPunchCards', event.target.checked)
                            }
                            label={t('BookingDiscounts.ChannelPunchCard')}
                        /> */}
                        {formik.touched.allowOnlineBooking && formik.errors.allowOnlineBooking && (
                            <Typography variant="caption" color="error">
                                {t(formik.errors.allowOnlineBooking as string)}
                            </Typography>
                        )}
                    </Grid2>
                </Grid2>
            </form>
        </Stack>
    );
}
